import { useCallback, useEffect, useRef, useState } from 'react';
import { getFieldIdsOverText, measureCanvasFields } from '../utility/fieldsOverText';

const isSameSet = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));

// The ids of the "Always on Top" fields drawn over a text field on the constructor
// canvas inside `containerRef` (see utility/fieldsOverText).
//
// A field's place on the canvas is not all in `fields`: a drag, a resize or a text
// growing with what is typed into it moves the field on the canvas before (or without)
// anything being written back to the form. So the canvas is measured again whenever it
// changes - a field's inline position or size, a field added or removed, the canvas
// resized - at most once a frame, and after the form itself changes.
export const useFieldsOverText = (containerRef, fields) => {
  const [fieldIds, setFieldIds] = useState(() => new Set());
  const fieldsRef = useRef(fields);
  const frameRef = useRef(null);

  fieldsRef.current = fields;

  const scheduleMeasure = useCallback(() => {
    if (frameRef.current !== null) return;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;

      const container = containerRef.current;
      if (!container) return;

      const nextIds = getFieldIdsOverText(measureCanvasFields(container, fieldsRef.current));
      setFieldIds((currentIds) => (isSameSet(currentIds, nextIds) ? currentIds : nextIds));
    });
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleMeasure);
    resizeObserver?.observe(container);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [containerRef, scheduleMeasure]);

  useEffect(() => {
    scheduleMeasure();
  }, [fields, scheduleMeasure]);

  return fieldIds;
};
